import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Input from '../forms/Input';
import Select from '../forms/Select';
import TextArea from '../forms/TextArea';
import { trackEvent } from '../../lib/analytics';

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const roleOptions = [
  { value: 'director', label: 'Centre Director / Owner' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      trackEvent('demo_form_opened');
    }
  }, [isOpen]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    school_name: '',
    role: '',
    num_children: '',
    message: '',
    email_consent: false,
    website_url: '', // honeypot
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.email_consent) newErrors.email_consent = 'You must agree to be contacted';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const { leadsApi } = await import('../../services/api/leads');
      await leadsApi.submitContact({
        ...formData,
        num_children: formData.num_children ? parseInt(formData.num_children) : undefined,
      });
      setSubmitted(true);
      trackEvent('demo_form_submitted', {
        role: formData.role || undefined,
        has_school_name: Boolean(formData.school_name),
        has_phone: Boolean(formData.phone),
        num_children: formData.num_children ? parseInt(formData.num_children) : undefined,
      });
    } catch {
      // Error handled by global handler
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleClose = () => {
    if (submitted) {
      setSubmitted(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        school_name: '',
        role: '',
        num_children: '',
        message: '',
        email_consent: false,
        website_url: '',
      });
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Book a Demo" size="lg">
      {submitted ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-display font-semibold text-gray-900 mb-2">Thank you!</h3>
          <p className="text-gray-600">
            We've received your demo request and will be in touch within 24 hours.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot - hidden from humans */}
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <input
              type="text"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={e => handleChange('website_url', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={formData.first_name}
              onChange={e => handleChange('first_name', e.target.value)}
              placeholder="Jane"
            />
            <Input
              label="Last Name"
              value={formData.last_name}
              onChange={e => handleChange('last_name', e.target.value)}
              placeholder="Director"
            />
          </div>

          <Input
            label="Email"
            type="email"
            required
            value={formData.email}
            onChange={e => handleChange('email', e.target.value)}
            error={errors.email}
            placeholder="jane@school.co.za"
          />

          <Input
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={e => handleChange('phone', e.target.value)}
            placeholder="082 123 4567"
          />

          <Input
            label="School / Centre Name"
            value={formData.school_name}
            onChange={e => handleChange('school_name', e.target.value)}
            placeholder="Sunshine ECD Centre"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Your Role"
              value={formData.role}
              onChange={e => handleChange('role', e.target.value)}
              options={roleOptions}
              placeholder="Select your role"
            />
            <Input
              label="Number of Children"
              type="number"
              value={formData.num_children}
              onChange={e => handleChange('num_children', e.target.value)}
              placeholder="e.g. 30"
            />
          </div>

          <TextArea
            label="Message (optional)"
            value={formData.message}
            onChange={e => handleChange('message', e.target.value)}
            placeholder="Tell us about your centre and what you're looking for..."
            rows={3}
          />

          <label className="flex items-start space-x-2">
            <input
              type="checkbox"
              checked={formData.email_consent}
              onChange={e => handleChange('email_consent', e.target.checked)}
              className="mt-1 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              required
            />
            <span className="text-sm text-gray-600">
              I agree to be contacted about Sprout and consent to the processing of my personal information in accordance with POPIA.
              {errors.email_consent && <span className="text-red-600 block">{errors.email_consent}</span>}
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Request a Demo'}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default DemoRequestModal;
